/**
 * ConfirmDialogComponent
 *
 * A reusable confirmation dialog using Angular Material Dialog.
 * Supports customizable title, message, and button labels with i18n.
 *
 * @feature 011-patient-lifecycle-permissions
 * @tasks T015-T018
 */
import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

/**
 * Configuration data for the confirm dialog
 */
export interface ConfirmDialogData {
  /** Translation key for the dialog title */
  titleKey: string;
  /** Translation key for the message */
  messageKey: string;
  /** Translation key for the confirm button (default: 'COMMON.CONFIRM') */
  confirmKey?: string;
  /** Translation key for the cancel button (default: 'COMMON.CANCEL') */
  cancelKey?: string;
  /** Type of confirmation - affects styling (default: 'warning') */
  type?: 'warning' | 'danger' | 'info';
  /** Icon to display (optional) */
  icon?: string;
  /** Translation params for the message */
  messageParams?: Record<string, string>;
  /** Whether to show the cancel button (default: true) */
  showCancelButton?: boolean;
}

/**
 * Result returned when the dialog closes
 */
export interface ConfirmDialogResult {
  confirmed: boolean;
}

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule
  ],
  templateUrl: './confirm-dialog.component.html',
  styleUrls: ['./confirm-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConfirmDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ConfirmDialogData,
    private dialogRef: MatDialogRef<ConfirmDialogComponent, ConfirmDialogResult>
  ) {}

  /**
   * Get the confirm button translation key
   */
  get confirmKey(): string {
    return this.data.confirmKey || 'COMMON.CONFIRM';
  }

  /**
   * Get the cancel button translation key
   */
  get cancelKey(): string {
    return this.data.cancelKey || 'COMMON.CANCEL';
  }

  /**
   * Get the dialog type for styling
   */
  get dialogType(): string {
    return this.data.type || 'warning';
  }

  /**
   * Get the icon to display
   */
  get icon(): string | null {
    if (this.data.icon) {
      return this.data.icon;
    }
    // Default icons based on type
    switch (this.dialogType) {
      case 'danger':
        return 'warning';
      case 'info':
        return 'info';
      case 'warning':
      default:
        return 'help_outline';
    }
  }

  /**
   * Whether to show the cancel button
   */
  get showCancelButton(): boolean {
    return this.data.showCancelButton !== false;
  }

  /**
   * Handle confirm button click
   */
  onConfirm(): void {
    this.dialogRef.close({ confirmed: true });
  }

  /**
   * Handle cancel button click
   */
  onCancel(): void {
    this.dialogRef.close({ confirmed: false });
  }
}
